import * as THREE from 'three';
import type { World } from '../sim/world';
import type { Intent } from '../sim/captain';

// A word over each enemy near enough to matter, for what she is about with
// her guns toward us (render/threat.ts paints the same on the water): coming
// round, her guns bearing, or reloading - so the moment to put the helm over,
// or to close while she can't answer, can be seen coming.

interface Tag {
  el: HTMLElement;
  text: string;
  cls: string;
}

export class IntentTags {
  private readonly wrap: HTMLElement;
  private readonly tags = new Map<number, Tag>();
  private readonly was = new Map<number, Intent['state']>();
  private readonly p = new THREE.Vector3();
  /** Her guns have just come to bear on us (for the log). */
  onBear: (vessel: number, side: 1 | -1) => void = () => {};

  constructor(parent: HTMLElement) {
    this.wrap = document.createElement('div');
    this.wrap.className = 'intent-tags';
    parent.appendChild(this.wrap);
  }

  update(world: World, camera: THREE.PerspectiveCamera, intents: ReadonlyMap<number, Intent>): void {
    const w = window.innerWidth, h = window.innerHeight;
    for (const [id, it] of intents) {
      const before = this.was.get(id);
      if (it.state === 'bearing' && before !== 'bearing') this.onBear(id, it.side);
      this.was.set(id, it.state);
    }
    for (const id of [...this.was.keys()]) if (!intents.has(id)) this.was.delete(id);
    for (const [id, tag] of this.tags) {
      if (!intents.has(id)) {
        tag.el.remove();
        this.tags.delete(id);
      }
    }
    for (const [id, it] of intents) {
      const v = world.vessels.find((o) => o.id === id);
      let text = '', cls = '';
      if (it.state === 'bearing') (text = `${it.side > 0 ? 'Starboard' : 'Port'} guns bear on us`), (cls = 'bad');
      else if (it.state === 'coming') (text = it.presenting !== 0 ? 'Coming round to fire' : 'Guns run out'), (cls = 'warn');
      else if (it.state === 'reloading') (text = `Reloading ${Math.round(it.reload * 100)}%`), (cls = 'dim');
      let tag = this.tags.get(id);
      if (!text || !v) {
        if (tag) tag.el.style.display = 'none';
        continue;
      }
      if (!tag) {
        const el = document.createElement('div');
        this.wrap.appendChild(el);
        tag = { el, text: '', cls: '' };
        this.tags.set(id, tag);
      }
      if (tag.text !== text) (tag.el.textContent = text), (tag.text = text);
      if (tag.cls !== cls) (tag.el.className = `intent-tag ${cls}`), (tag.cls = cls);
      // Over her mastheads.
      const s = v.ship;
      const q = this.p.set(s.com.x, s.com.y + 27 * s.sy, s.com.z).project(camera);
      if (q.z > 1 || Math.abs(q.x) > 1.05 || Math.abs(q.y) > 1.05) {
        tag.el.style.display = 'none';
        continue;
      }
      tag.el.style.display = '';
      const x = (q.x * 0.5 + 0.5) * w, y = (-q.y * 0.5 + 0.5) * h;
      tag.el.style.transform = `translate(${Math.round(x)}px, ${Math.round(Math.max(y, 70))}px) translate(-50%, -100%)`;
    }
  }
}
